import { auth_credentials, auth_headers } from './auth-transport.js';

/**
 * Retrieves a cookie value by its name.
 * @param {string} name - The name of the cookie to retrieve.
 * @returns {string|null} - The value of the cookie, or null if not found.
 */
function get_cookie(name) {
    let cookie_value = null;
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            // Does this cookie string begin with the name we want?
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                cookie_value = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookie_value;
}

export async function api_request(method, path, data = null, extraHeaders = {}, { signal, keepalive = false } = {}) {
    const csrf_token = get_cookie('csrftoken');

    const request_headers = {
        Accept: 'application/json',
        ...extraHeaders, // Apply any custom headers first
        ...auth_headers(), // Bearer token in the Telegram Mini App; nothing on web.
    };

    if (csrf_token) {
        request_headers['X-CSRFToken'] = csrf_token;
    }

    const options = {
        method,
        headers: request_headers,
        credentials: auth_credentials(),
    };

    if (signal) options.signal = signal;
    if (keepalive) options.keepalive = true;

    if (data !== null) {
        if (data instanceof FormData) {
            delete request_headers['Content-Type']; 
            options.body = data;
        } else {
            request_headers['Content-Type'] = 'application/json';
            options.body = JSON.stringify(data);
        }
    }

    const response = await fetch(`/api${path}`, options); 

    if (response.status === 204) {
        return { success: true };
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
        const response_data = await response.json();
        if (!response.ok) {
            const error = new Error(response_data.detail || response_data.message || `API Error: ${response.status}`);
            // Callers distinguish typed HTTP failures (for example paywall 403 vs state conflict 409).
            error.status = response.status;
            error.detail = response_data.detail;
            error.code = response_data.code || null;
            error.data = response_data;
            throw error;
        }
        return {'status': response.status, 'response_data': response_data};
    } else {
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`API Error: ${response.status} - ${errorText || response.statusText}`);
        }
        return { success: true, message: 'No content received or not JSON.' };
    }
}



/**
 * Fetches a CSRF token from the server.
 * @returns {Promise<object>} - The response containing the CSRF token.
 */
export async function get_csrf_token() {
    return api_request('GET', '/csrf');
}


/**
 * Fetches the currently authenticated user's data.
 * @returns {Promise<object>} - The current user's data.
 */
export async function get_current_user() {
    return api_request('GET', '/auth/me');
}

/**
 * Initializes the app: sets CSRF cookie, returns user + config.
 * Replaces the two-call pattern (get_csrf_token + get_current_user).
 * @returns {Promise<object>}
 */
export async function init_app() {
    // index.html starts this request before the bundle exists. Adopt it once;
    // a retry after a failure — or the Telegram entry, which has no preflight —
    // goes through api_request like any other call.
    const preflight = window.__init_request;
    if (preflight) {
        window.__init_request = null;
        try {
            return { status: 200, response_data: await preflight };
        } catch {
            // Raced a cold worker or a flaky first connection; ask again.
        }
    }
    return api_request('GET', '/init');
}

/**
 * Trades Telegram's signed `initData` for a bearer token.
 * @param {string} init_data Raw `window.Telegram.WebApp.initData`, unmodified —
 *   the HMAC covers the exact field set Telegram signed.
 * @param {string} [start_param] Deep-link payload, recorded as the signup source.
 */
export async function telegram_auth(init_data, start_param = '') {
    return api_request('POST', '/telegram/auth', { init_data, start_param });
}

/**
 * Sends a one-time code to an address a Telegram user claims.
 * The response is identical whether or not an account already holds it — the
 * server will not confirm existence before the code is proven.
 * @param {string} email
 */
export async function telegram_email_start(email) {
    return api_request('POST', '/telegram/email/start', { email });
}

/**
 * Proves the address, which either claims it for the Telegram-provisioned
 * account or links that account to the one which already held it.
 * @param {string} email
 * @param {string} code
 * @returns {Promise<{response_data: {user: object, token: string, linked_existing_account: boolean, needs_academic: boolean}}>}
 */
export async function telegram_email_verify(email, code) {
    return api_request('POST', '/telegram/email/verify', { email, code });
}

/**
 * Confirms a web signup with the code that was mailed to the address. The
 * password goes with it because confirming is what sets it: the one stored at
 * signup could have been chosen by anyone who typed this address.
 */
export async function verify_email(email, code, password) {
    return api_request('POST', '/auth/verify-email', { email, code, password });
}

/** Re-sends a signup code. Always reports success, whatever the address. */
export async function resend_verification_code(email) {
    return api_request('POST', '/auth/resend-code', { email });
}

/**
 * Asks for a password-reset code. Always reports success — the server will not
 * say whether the address has an account, so the client must not either.
 * @param {string} email
 */
export async function request_password_reset(email) {
    return api_request('POST', '/auth/password-reset/request', { email });
}

/**
 * Sets a new password against a mailed code. Signs the account out everywhere.
 * @param {string} email
 * @param {string} code
 * @param {string} new_password
 */
export async function confirm_password_reset(email, code, new_password) {
    return api_request('POST', '/auth/password-reset/confirm', { email, code, new_password });
}

/** Mails a confirmation code to the signed-in account's own address. */
export async function start_own_email_verification() {
    return api_request('POST', '/auth/me/email/verify/start');
}

/**
 * Confirms the signed-in account's own address.
 * @param {string} code
 * @returns {Promise<object>} The updated user.
 */
export async function confirm_own_email_verification(code) {
    return api_request('POST', '/auth/me/email/verify/confirm', { code });
}

/**
 * Detaches the Telegram account from the signed-in user.
 * Refused with 409 when Telegram is the only way left to sign in.
 * @returns {Promise<object>} The updated user, re-resolved without the link.
 */
export async function unlink_telegram() {
    return api_request('DELETE', '/auth/me/telegram');
}

/** Mails a password-reset code to the signed-in account. Requires a confirmed address. */
export async function start_own_password_reset() {
    return api_request('POST', '/auth/me/password-reset/start');
}

/**
 * Changes the signed-in user's password. Other sessions are dropped; this one
 * survives.
 * @param {string} code
 * @param {string} new_password
 */
export async function confirm_own_password_reset(code, new_password) {
    return api_request('POST', '/auth/me/password-reset/confirm', { code, new_password });
}

/**
 * Logs in a user.
 * @param {string} email
 * @param {string} password
 * @returns {Promise<object>}
 */
export async function login(email, password) {
    return api_request('POST', '/auth/login', { email, password });
}

/**
 * Registers a new user.
 * @param {string} email
 * @param {string} password
 * @returns {Promise<object>}
 */
export async function register(email, password, extra = {}) {
    return api_request('POST', '/auth/register', { email, password, ...extra });
}

/**
 * Logs out the current user.
 * @returns {Promise<object>}
 */
export async function logout() {
    return api_request('POST', '/auth/logout');
}

/**
 * Updates the current user's profile (faculty, study year, birth date).
 * @param {object} data - { faculty_id?, study_year_id?, birth_date? }
 * @returns {Promise<object>}
 */
export async function update_profile(data) {
    return api_request('PUT', '/auth/me', data);
}


/**
 * Uploads the current user's profile picture.
 * @param {File} file - PNG, JPEG or WebP image, max 5 MB.
 * @param {{x: number, y: number, size: number}|null} crop - Square region of the
 *   source to use, in source pixels. Omit for the largest centred square.
 * @returns {Promise<object>} - The updated user.
 */
export async function upload_avatar(file, crop = null) {
    const formData = new FormData();
    formData.append('file', file);
    if (crop) {
        formData.append('crop_x', String(crop.x));
        formData.append('crop_y', String(crop.y));
        formData.append('crop_size', String(crop.size));
    }
    return api_request('POST', '/auth/me/avatar', formData);
}


/**
 * Re-frames the existing picture from its stored original — no re-upload.
 * @param {{x: number, y: number, size: number}} crop
 * @returns {Promise<object>} - The updated user.
 */
export async function recrop_avatar(crop) {
    return api_request('PUT', '/auth/me/avatar/crop', crop);
}


/**
 * Removes the uploaded profile picture, falling back to the social avatar.
 * @returns {Promise<object>} - The updated user.
 */
export async function delete_avatar() {
    return api_request('DELETE', '/auth/me/avatar');
}

/**
 * Fetches active session details for a user. (Requires SuperuserAuth)
 * @param {number} user_id The ID of the user.
 * @returns {Promise<object>} A promise that resolves to the session details.
 */
export async function get_user_session(user_id) {
    return await api_request('GET', `/users/${user_id}/session`);
}

/**
 * Deletes a user's active session, forcing logout. (Requires SuperuserAuth)
 * @param {number} user_id The ID of the user.
 * @returns {Promise<object>} A promise indicating success.
 */
export async function delete_user_session(user_id) {
    return await api_request('DELETE', `/users/${user_id}/session`);
}


////////////////////////////////////////////////////////////
///////////////// API Calls for Courses
////////////////////////////////////////////////////////////

/**
 * Fetches a list of all courses.
 * @returns {Promise<object>} A promise that resolves to the API response containing a list of courses.
 */
export async function list_courses(year = null) {
    const qs = year != null ? `?year=${year}` : '';
    return await api_request('GET', `/courses${qs}`);
}


/**
 * Creates a new course.
 * @param {string} title - The title of the course.
 * @param {string} description - The description of the course.
 * @param {number} price - The price of the course.
 * @param {File} [thumbnail] - The thumbnail image file for the course (optional).
 * @returns {Promise<object>} - The newly created course data.
 */
export async function create_course(title, description, price, thumbnail = null, study_year_id = null) {
    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', description);
    formData.append('price', price);
    if (study_year_id != null) formData.append('study_year_id', study_year_id);

    if (thumbnail) {
        formData.append('thumbnail', thumbnail);
    }

    return api_request('POST', '/courses', formData);
}

/**
 * Fetches a single course by its ID.
 * @param {number} course_id The ID of the course to retrieve.
 * @returns {Promise<object>} A promise that resolves to the API response containing the course details.
 */
export async function get_course(course_id) {
    return await api_request('GET', `/courses/${course_id}`);
}

/**
 * Fetches the course's lectures ranked by how many questions they hold.
 * @param {number} course_id The ID of the course.
 * @returns {Promise<object>} A promise resolving to `{ total_questions, items }`.
 */
export async function get_course_question_frequency(course_id) {
    return await api_request('GET', `/courses/${course_id}/question-frequency`);
}

/**
 * Updates an existing course.
 * @param {number} course_id The ID of the course to update.
 * @param {object} form_data An object containing the updated course data (e.g., name, description) and optionally a new thumbnail file.
 *                           Properties that are `File` objects will be handled as file uploads.
 * @returns {Promise<object>} A promise that resolves to the API response containing the updated course.
 */
export async function update_course(course_id, form_data) {
    const data = new FormData();
    for (const key in form_data) {
        if (form_data[key] instanceof File) {
            data.append(key, form_data[key], form_data[key].name);
        } else {
            data.append(key, form_data[key]);
        }
    }
    return await api_request('PUT', `/courses/${course_id}`, data, { 'Content-Type': undefined }); // Let fetch set Content-Type for FormData
}

/**
 * Deletes a course by its ID.
 * @param {number} course_id The ID of the course to delete.
 * @returns {Promise<object>} A promise that resolves to the API response indicating success or failure of the deletion.
 */
export async function delete_course(course_id) {
    return await api_request('DELETE', `/courses/${course_id}`);
}



////////////////////////////////////////////////////////////
///////////////// API Calls for Users
////////////////////////////////////////////////////////////


/**
 * Fetches a list of all users. (Requires SuperuserAuth)
 * @param {number} [page=1]
 * @param {number} [page_size=20]
 * @returns {Promise<object>} A promise that resolves to {items, total, page, page_size, total_pages}.
 */
export async function list_users(page = 1, page_size = 20) {
    return await api_request('GET', `/users?page=${page}&page_size=${page_size}`);
}

/**
 * Creates a new user. (Requires SuperuserAuth)
 * @param {object} data An object containing the data for the new user (e.g., username, email, password).
 * @returns {Promise<object>} A promise that resolves to the API response containing the newly created user.
 */
export async function create_user(data) {
    return await api_request('POST', '/users/create', data);
}

/**
 * Fetches a single user by their ID. (Requires SuperuserAuth)
 * @param {number} user_id The ID of the user to retrieve.
 * @returns {Promise<object>} A promise that resolves to the API response containing the user details.
 */
export async function get_user(user_id) {
    return await api_request('GET', `/users/${user_id}`);
}

/**
 * Updates an existing user. (Requires SuperuserAuth)
 * @param {number} user_id The ID of the user to update.
 * @param {object} data An object containing the updated user data.
 * @returns {Promise<object>} A promise that resolves to the API response containing the updated user.
 */
export async function update_user(user_id, data) {
    return await api_request('PUT', `/users/${user_id}`, data);
}

/**
 * Deletes a user by their ID. (Requires SuperuserAuth)
 * @param {number} user_id The ID of the user to delete.
 * @returns {Promise<object>} A promise that resolves to the API response indicating success or failure of the deletion.
 */
export async function delete_user(user_id) {
    return await api_request('DELETE', `/users/${user_id}`);
}


////////////////////////////////////////////////////////////
///////////////// API Calls for Faculties
////////////////////////////////////////////////////////////

export async function list_public_faculties() {
    return await api_request('GET', '/public/faculties');
}

export async function list_faculties(page = 1, page_size = 20, search = '') {
    let url = `/faculties?page=${page}&page_size=${page_size}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    return await api_request('GET', url);
}

export async function create_faculty(data) {
    return await api_request('POST', '/faculties', data);
}

export async function get_faculty(faculty_id) {
    return await api_request('GET', `/faculties/${faculty_id}`);
}

export async function update_faculty(faculty_id, data) {
    return await api_request('PUT', `/faculties/${faculty_id}`, data);
}

export async function delete_faculty(faculty_id) {
    return await api_request('DELETE', `/faculties/${faculty_id}`);
}


////////////////////////////////////////////////////////////
///////////////// API Calls for Study Years
////////////////////////////////////////////////////////////

export async function list_public_study_years({ withContent = false } = {}) {
    const qs = withContent ? '?with_content=true' : '';
    return await api_request('GET', `/public/study-years${qs}`);
}

export async function list_study_years(page = 1, page_size = 20) {
    return await api_request('GET', `/study-years?page=${page}&page_size=${page_size}`);
}

export async function create_study_year(data) {
    return await api_request('POST', '/study-years', data);
}

export async function get_study_year(study_year_id) {
    return await api_request('GET', `/study-years/${study_year_id}`);
}

export async function update_study_year(study_year_id, data) {
    return await api_request('PUT', `/study-years/${study_year_id}`, data);
}

export async function delete_study_year(study_year_id) {
    return await api_request('DELETE', `/study-years/${study_year_id}`);
}


////////////////////////////////////////////////////////////
///////////////// API Calls for Lectures
////////////////////////////////////////////////////////////

/**
 * Fetches a list of all lectures for a specific course.
 */
export async function list_lectures_for_course(course_id) {
    return await api_request('GET', `/courses/${course_id}/lectures`);
}

export async function get_lecture_by_id(lecture_id) {
    const { response_data } = await api_request('GET', `/lectures/${lecture_id}`);
	return response_data;
}

/**
 * Fetches a single lecture by its ID.
 * @param {number} lecture_id The ID of the lecture to retrieve.
 * @returns {Promise<object>} A promise that resolves to the API response containing the lecture details.
 */
export async function get_lecture(lecture_id) {
    return await api_request('GET', `/lectures/${lecture_id}`);
}

/**
 * Creates a new lecture for a specific course.
 * @param {number} course_id The ID of the course.
 * @param {object} data The lecture data, including title, order, is_premium, and attachments.
 */
export async function create_lecture_for_course(course_id, data) {
    return await api_request('POST', `/courses/${course_id}/lectures`, data);
}

/**
 * Updates an existing lecture.
 * @param {number} course_id The ID of the course.
 * @param {number} lecture_id The ID of the lecture to update.
 * @param {object} data The updated lecture data.
 */
export async function update_lecture(course_id, lecture_id, data) {
    return await api_request('PUT', `/courses/${course_id}/lectures/${lecture_id}`, data);
}

/**
 * Deletes a lecture by its ID.
 */
export async function delete_lecture(course_id, lecture_id) {
    return await api_request('DELETE', `/courses/${course_id}/lectures/${lecture_id}`);
}

/**
 * Sends the new order of lectures to the backend.
 * @param {number} course_id The ID of the course.
 * @param {number[]} lecture_ids An array of lecture IDs in the new order.
 */
export async function reorder_lectures(course_id, lecture_ids) {
    return await api_request('POST', `/courses/${course_id}/lectures/reorder`, { lecture_ids });
}


////////////////////////////////////////////////////////////
///////////////// API Calls for Subscriptions
////////////////////////////////////////////////////////////

/**
 * Fetches a list of all subscription keys. (Requires StaffAuth)
 * @param {number} [page=1]
 * @param {number} [page_size=20]
 * @returns {Promise<object>} A promise that resolves to {items, total, page, page_size, total_pages}.
 */
export async function list_subscription_keys(page = 1, page_size = 20) {
    return await api_request('GET', `/subscription-keys?page=${page}&page_size=${page_size}`);
}

/**
 * Creates a new subscription key. (Requires StaffAuth)
 * @param {object} data An object containing the data for the new key (e.g., course_id, duration_days).
 * @returns {Promise<object>} A promise that resolves to the API response containing the newly created subscription key.
 */
export async function create_subscription_key(data) {
    return await api_request('POST', '/subscription-keys', data);
}

/**
 * Fetches a single subscription key by its UUID. (Requires StaffAuth)
 * @param {string} key_uuid The UUID of the subscription key to retrieve.
 * @returns {Promise<object>} A promise that resolves to the API response containing the subscription key details.
 */
export async function get_subscription_key(key_uuid) {
    return await api_request('GET', `/subscription-keys/${key_uuid}`);
}

/**
 * Updates an existing subscription key. (Requires StaffAuth)
 * @param {string} key_uuid The UUID of the subscription key to update.
 * @param {object} data An object containing the updated key data (e.g., course_id, duration_days, is_redeemed).
 * @returns {Promise<object>} A promise that resolves to the API response containing the updated subscription key.
 */
export async function update_subscription_key(key_uuid, data) {
    return await api_request('PUT', `/subscription-keys/${key_uuid}`, data);
}

/**
 * Deletes a subscription key by its UUID. (Requires StaffAuth)
 * @param {string} key_uuid The UUID of the subscription key to delete.
 * @returns {Promise<object>} A promise that resolves to the API response indicating success or failure of the deletion.
 */
export async function delete_subscription_key(key_uuid) {
    return await api_request('DELETE', `/subscription-keys/${key_uuid}`);
}


/**
 * Redeems a subscription key for the authenticated user. (Requires SessionAuth)
 * @param {object} data An object containing the key to redeem (e.g., { key: "some-uuid-string" }).
 * @returns {Promise<object>} A promise that resolves to the API response containing the newly created or updated subscription.
 */
export async function redeem_subscription_key(data) {
    return await api_request('POST', '/subscriptions/redeem', data);
}


/**
 * Unlocks a course or book without a key. (Requires SuperuserAuth)
 * @param {object} data { content_type: 'course'|'book', object_id: number, user_id?: number, duration_days?: number }
 * @returns {Promise<object>} A promise that resolves to the created or extended subscription.
 */
export async function admin_unlock_content(data) {
    return await api_request('POST', '/subscriptions/admin-unlock', data);
}


/**
 * Fetches a list of all subscriptions across all users. (Requires SuperuserAuth)
 * @param {number} [page=1]
 * @param {number} [page_size=20]
 * @returns {Promise<object>} A promise that resolves to {items, total, page, page_size, total_pages}.
 */
export async function list_all_subscriptions(page = 1, page_size = 20) {
    return await api_request('GET', `/subscriptions?page=${page}&page_size=${page_size}`);
}

export async function list_refunds(page = 1, page_size = 20) {
    return await api_request('GET', `/admin/refunds?page=${page}&page_size=${page_size}`);
}

export async function issue_refund({ subscription_id, amount, reason = 'REQUESTED', notes = '' }) {
    return await api_request('POST', '/admin/refunds', { subscription_id, amount, reason, notes });
}

export async function void_refund(refund_id) {
    return await api_request('DELETE', `/admin/refunds/${refund_id}`);
}

/**
 * Fetches a single subscription by its ID. (Requires SuperuserAuth)
 * @param {number} subscription_id The ID of the subscription to retrieve.
 * @returns {Promise<object>} A promise that resolves to the API response containing the subscription details.
 */
export async function get_subscription_by_id(subscription_id) {
    return await api_request('GET', `/subscriptions/${subscription_id}`);
}

/**
 * Fetches a list of all subscriptions for a specific user. (Requires SuperuserAuth)
 * @param {number} user_id The ID of the user whose subscriptions are to be retrieved.
 * @returns {Promise<object>} A promise that resolves to the API response containing a list of subscriptions for the user.
 */
export async function list_user_subscriptions(user_id) {
    return await api_request('GET', `/users/${user_id}/subscriptions`);
}

export async function list_my_subscriptions() {
	return await api_request('GET', '/subscriptions/me');
}

////////////////////////////////////////////////////////////
///////////////// API Calls for chunked upload
////////////////////////////////////////////////////////////


/**
 * Initiates an upload for a SINGLE file.
 * @param {{filename: string, total_size: number}} payload
 * @returns {Promise<{upload_id: string, chunk_size: number}>}
 */
export async function initiate_upload(payload) {
    const { response_data } = await api_request('POST', '/uploads/init', payload);
    return response_data;
}

/**
 * Gets the status of an uploaded file.
 * @param {string} upload_id
 * @returns {Promise<{uploaded_chunks: number[], status: string}>}
 */
export async function get_upload_status(upload_id) {
    const { response_data } = await api_request('GET', `/uploads/${upload_id}`);
    return response_data;
}

/**
 * Finalizes a single file upload.
 * @param {{upload_id: string, title: string, mime_type: string}} payload
 * @returns {Promise<any>} The final Media object from the server.
 */
export async function finalize_upload(payload) {
    // Finalize uses FormData because the Django Ninja endpoint expects Form(...)
    const formData = new FormData();
    formData.append('title', payload.title);
    formData.append('mime_type', payload.mime_type);

    const { response_data } = await api_request('POST', `/uploads/${payload.upload_id}/finalize`, formData);
    return response_data;
}

/**
 * Cancels a single file upload and cleans up its resources on the server.
 * @param {string} upload_id
 */
export async function cancel_upload(upload_id) {
    await api_request('POST', `/uploads/${upload_id}/cancel`, {});
}

/**
 * Single-shot image upload for inline question-stem media.
 * Bypasses the chunked-upload flow. Superuser only.
 *
 * Uses XHR (not fetch) so it can report byte-level upload progress via
 * `on_progress(0..1)` — needed to render a real progress bar in the
 * media-link upload dialog. Also supports abort via `signal`.
 *
 * @param {File} file
 * @param {{ on_progress?: (progress: number) => void, signal?: AbortSignal }} [opts]
 * @returns {Promise<{status: number, response_data: {id: number, file_hash: string, mime_type: string}}>}
 */
/**
 * Lists uploaded question-stem images with per-row question reference counts.
 * @param {number} [page]
 * @param {number} [page_size]
 */
export async function list_question_images(page = 1, page_size = 24) {
    return api_request('GET', `/media/question-images?page=${page}&page_size=${page_size}`);
}

/**
 * Deletes a Media row by id. Used by the image picker's delete action.
 * (Reuses the existing DELETE /media/{id} endpoint.)
 * @param {number} media_id
 */
export async function delete_media_by_id(media_id) {
    return api_request('DELETE', `/media/${media_id}`);
}

export function upload_question_image(file, { on_progress, signal } = {}) {
    return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('file', file);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', '/api/media/image', true);
        xhr.responseType = 'json';

        const csrf_token = get_cookie('csrftoken');
        if (csrf_token) xhr.setRequestHeader('X-CSRFToken', csrf_token);
        xhr.setRequestHeader('Accept', 'application/json');
        xhr.withCredentials = true;

        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable && on_progress) {
                on_progress(event.loaded / event.total);
            }
        };

        xhr.onload = () => {
            const body = xhr.response;
            if (xhr.status >= 200 && xhr.status < 300) {
                on_progress?.(1);
                resolve({ status: xhr.status, response_data: body });
            } else {
                const msg = body?.detail || body?.message || `Upload failed: ${xhr.status} ${xhr.statusText}`;
                reject(new Error(msg));
            }
        };
        xhr.onerror = () => reject(new Error('Upload failed due to a network error.'));
        xhr.onabort = () => reject(Object.assign(new Error('Upload cancelled.'), { name: 'AbortError' }));

        if (signal) {
            if (signal.aborted) {
                xhr.abort();
                return;
            }
            signal.addEventListener('abort', () => xhr.abort(), { once: true });
        }

        xhr.send(formData);
    });
}


/**
 * Uploads a single chunk for a specific file using XMLHttpRequest for progress.
 * @param {{upload_id: string, chunk_number: number, chunk_blob: Blob}} payload
 * @param {{on_progress?: (progress: number) => void, signal?: AbortSignal}} options
 */
export function upload_chunk(
    { upload_id, chunk_number, chunk_blob },
    { on_progress, signal } = {}
) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        // The endpoint now includes the upload_id directly
        xhr.open('POST', `/api/uploads/${upload_id}/chunk/${chunk_number}`, true);
        xhr.responseType = 'json';

        // Add CSRF token header for Django
        const csrf_token = document.cookie.split('; ').find(row => row.startsWith('csrftoken='))?.split('=')[1];
        if (csrf_token) {
            xhr.setRequestHeader('X-CSRFToken', csrf_token);
        }

        xhr.upload.onprogress = event => {
            if (event.lengthComputable) {
                on_progress?.(event.loaded);
            }
        };

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve(xhr.response);
            } else {
                const error_message = xhr.response?.message || `Chunk upload failed: ${xhr.status} ${xhr.statusText}`;
                reject(new Error(error_message));
            }
        };

        xhr.onerror = () => reject(new Error('Chunk upload failed due to a network error.'));
        xhr.onabort = () => reject(new DOMException('Upload aborted by user.', 'AbortError'));

        if (signal) {
            const on_signal_abort = () => xhr.abort();
            signal.addEventListener('abort', on_signal_abort, { once: true });
            xhr.onloadend = () => signal.removeEventListener('abort', on_signal_abort);
        }

        const formData = new FormData();
        // The backend expects the file to be named 'file'
        formData.append('file', chunk_blob, 'chunk.bin');
        xhr.send(formData);
    });
}


export async function list_all_media(page = 1, page_size = 20) {
    try {
        const result = await api_request('GET', `/media?page=${page}&page_size=${page_size}`);
        return result.response_data;

    } catch (error) {
        console.error('Error in list_all_media API call:', error);
        throw error;
    }
}


/**
 * Deletes a media item by its ID.
 * @param {string | number} media_id The ID of the media to delete.
 * @returns {Promise<void>}
 */
export async function delete_media(media_id) {
    try {
        await api_request('DELETE', `/media/${media_id}`);
    } catch (error) {
        console.error(`Error deleting media item ${media_id}:`, error);
        throw error; // Re-throw to be caught by the component
    }
}

/**
 * Renames a media item by its ID.
 * @param {string | number} media_id The ID of the media to rename.
 * @param {string} new_name The new name for the media file.
 * @returns {Promise<object>} The updated media object from the API.
 */
export async function rename_media(media_id, new_name) {
    try {
        // The payload must match the MediaRenameIn schema on the backend.
        const payload = { new_name: new_name };
        
        // Use the generic api_request helper to make the PATCH call.
        const result = await api_request('PATCH', `/media/${media_id}`, payload);
        
        // On success, return the updated media data.
        return result.response_data;

    } catch (error) {
        console.error(`Error renaming media item ${media_id}:`, error);
        // Re-throw the error so the calling component can handle it (e.g., show a notification).
        throw error;
    }
}

////////////////////////////////////////////////////////////
///////////////// API Calls for Stats
////////////////////////////////////////////////////////////

/**
 * Fetches system and application statistics for the admin dashboard.
 * Requires SuperuserAuth.
 * @returns {Promise<object>} A promise that resolves to the API response containing the stats.
 */
export async function get_admin_stats() {
    const { response_data } = await api_request('GET', '/stats');
    return response_data;
}

////////////////////////////////////////////////////////////
///////////////// API Calls for Books 
////////////////////////////////////////////////////////////

/**
 * Fetches a list of all books.
 * @returns {Promise<object>} A promise that resolves to the API response containing a list of books.
 */
export async function list_books(year = null) {
    const qs = year != null ? `?year=${year}` : '';
    return await api_request('GET', `/books${qs}`);
}

/**
 * Fetches a single book by its ID.
 * @param {number} book_id The ID of the book to retrieve.
 * @returns {Promise<object>} A promise that resolves to the API response containing the book details.
 */
export async function get_book(book_id) {
    return await api_request('GET', `/books/${book_id}`);
}

/**
 * Fetches the processed HTML content for a book.
 * This is a special case as it expects a text/html response, not JSON.
 * @param {number} book_id The ID of the book content to retrieve.
 * @returns {Promise<string>} A promise that resolves to the HTML content as a string.
 */
export async function get_book_content(book_id) {
    // We cannot use the standard api_request here because it expects JSON.
    const response = await fetch(`/api/books/${book_id}/content`, {
        method: 'GET',
        headers: {
            'Accept': 'text/html',
            ...auth_headers(),
        },
        credentials: auth_credentials(),
    });

    if (!response.ok) {
        // Try to parse error as JSON, otherwise use status text
        try {
            const errorData = await response.json();
            throw new Error(errorData.detail || `API Error: ${response.status}`);
        } catch (e) {
            throw new Error(`API Error: ${response.status} - ${response.statusText}`);
        }
    }
    
    return await response.text();
}

/**
 * Creates a new book. (For admin panel)
 * @param {FormData} formData - FormData object containing book details and optional cover_image.
 * @returns {Promise<object>} - The newly created book data.
 */
export async function create_book(formData) {
    return api_request('POST', '/books', formData);
}

/**
 * Deletes a book by its ID. (For admin panel)
 * @param {number} book_id The ID of the book to delete.
 * @returns {Promise<object>} A promise indicating success.
 */
export async function delete_book(book_id) {
    return await api_request('DELETE', `/books/${book_id}`);
}

/**
 * Updates an existing book. (For admin panel)
 * @param {number} book_id The ID of the book to update.
 * @param {FormData} formData FormData object containing updated details.
 * @returns {Promise<object>} The updated book data.
 */
export async function update_book(book_id, formData) {
    return api_request('PUT', `/books/${book_id}`, formData);
}

////////////////////////////////////////////////////////////
///////////////// Analytics
////////////////////////////////////////////////////////////

export async function list_analytics_events({ page = 1, pageSize = 20, event_name, user_id, start_date, end_date } = {}) {
    let url = `/analytics/events?page=${page}&page_size=${pageSize}`;
    if (event_name) url += `&event_name=${encodeURIComponent(event_name)}`;
    if (user_id) url += `&user_id=${user_id}`;
    if (start_date) url += `&start_date=${start_date}`;
    if (end_date) url += `&end_date=${end_date}`;
    return api_request('GET', url);
}

export async function get_analytics_event_counts() {
    return api_request('GET', '/analytics/events/counts');
}

////////////////////////////////////////////////////////////
///////////////// Progress API (TTA v3)
////////////////////////////////////////////////////////////

export async function get_lecture_progress(lecture_id) {
    return api_request('GET', `/progress/lecture/${lecture_id}`);
}

export async function update_lecture_progress(lecture_id, data) {
    return api_request('POST', `/progress/lecture/${lecture_id}`, data);
}

export async function get_course_progress(course_id) {
    return api_request('GET', `/progress/course/${course_id}`);
}

export async function get_my_progress() {
    return api_request('GET', `/progress/me`);
}

export async function get_learning_analytics() {
    return api_request('GET', `/analytics/learning`);
}

export async function record_video_event(data) {
    return api_request('POST', '/analytics/video/event', data);
}

export async function get_video_analytics() {
    return api_request('GET', '/analytics/videos');
}

export async function get_video_analytics_detail(media_id) {
    return api_request('GET', `/analytics/videos/${media_id}`);
}

export async function get_video_time_series(opts = {}) {
    const qs = _growth_params(opts);
    return api_request('GET', `/analytics/videos/time-series?${qs}`, null, {}, { signal: opts.signal });
}

// ── Breakdown / Distribution ───────────────────────────────────────────────
export async function get_breakdown(kind, opts = {}) {
    const params = new URLSearchParams();
    if (opts.days) params.set('days', String(opts.days));
    if (opts.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    const path = `/analytics/${kind}${qs ? `?${qs}` : ''}`;
    return api_request('GET', path, null, {}, { signal: opts.signal });
}

export async function get_business_analytics() {
    return api_request('GET', '/analytics/business');
}

function _growth_params({ period, granularity, start_date, end_date, comparison } = {}) {
    const params = new URLSearchParams();
    if (period) params.set('period', period);
    if (granularity) params.set('granularity', granularity);
    if (start_date) params.set('start_date', start_date);
    if (end_date) params.set('end_date', end_date);
    if (comparison) params.set('comparison', 'true');
    return params.toString();
}

export async function get_business_growth(opts = {}) {
    const qs = _growth_params(opts);
    return api_request('GET', `/analytics/business/growth?${qs}`, null, {}, { signal: opts.signal });
}

export async function get_user_growth(opts = {}) {
    const qs = _growth_params(opts);
    return api_request('GET', `/analytics/growth/users?${qs}`, null, {}, { signal: opts.signal });
}

export async function get_revenue_growth(opts = {}) {
    const qs = _growth_params(opts);
    return api_request('GET', `/analytics/growth/revenue?${qs}`, null, {}, { signal: opts.signal });
}

export async function get_subscription_growth(opts = {}) {
    const qs = _growth_params(opts);
    return api_request('GET', `/analytics/growth/subscriptions?${qs}`, null, {}, { signal: opts.signal });
}

export async function get_activity_rolling(opts = {}) {
    const days = opts.days ?? 30;
    return api_request('GET', `/analytics/activity/rolling?days=${days}`, null, {}, { signal: opts.signal });
}

export async function get_student_health() {
    return api_request('GET', '/analytics/students/health');
}

// ── Analytics (extended endpoints) ─────────────────────────────────────────

export async function get_activity_heatmap(opts = {}) {
    const params = new URLSearchParams();
    if (opts.startDate && opts.endDate) {
        params.set('start_date', opts.startDate);
        params.set('end_date', opts.endDate);
    } else {
        params.set('days', String(opts.days ?? 30));
    }
    return api_request('GET', `/analytics/activity/heatmap?${params.toString()}`, null, {}, { signal: opts.signal });
}

export async function get_recent_activity(opts = {}) {
    const limit = opts.limit ?? 20;
    return api_request('GET', `/analytics/activity/recent?limit=${limit}`, null, {}, { signal: opts.signal });
}

export async function get_session_duration_distribution(opts = {}) {
    return api_request('GET', '/analytics/sessions/duration-distribution', null, {}, { signal: opts.signal });
}

export async function get_revenue_by_faculty(opts = {}) {
    const days = opts.days ?? 30;
    return api_request('GET', `/analytics/revenue/breakdown/faculty?days=${days}`, null, {}, { signal: opts.signal });
}

export async function get_top_selling_content(opts = {}) {
    const params = new URLSearchParams();
    if (opts.content_type) params.set('content_type', opts.content_type);
    if (opts.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return api_request('GET', `/analytics/revenue/top-content${qs ? `?${qs}` : ''}`, null, {}, { signal: opts.signal });
}

export async function get_quiz_accuracy_trend(opts = {}) {
    const qs = _growth_params(opts);
    return api_request('GET', `/analytics/quiz/accuracy-trend?${qs}`, null, {}, { signal: opts.signal });
}

export async function get_quiz_difficulty_distribution(opts = {}) {
    return api_request('GET', '/analytics/quiz/difficulty-distribution', null, {}, { signal: opts.signal });
}

export async function get_quiz_topics(opts = {}) {
    const order = opts.order === 'strongest' ? 'strongest' : 'weakest';
    const params = new URLSearchParams();
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.min_attempts != null) params.set('min_attempts', String(opts.min_attempts));
    const qs = params.toString();
    return api_request('GET', `/analytics/quiz/topics/${order}${qs ? `?${qs}` : ''}`, null, {}, { signal: opts.signal });
}

export async function get_learning_time_to_complete(opts = {}) {
    return api_request('GET', '/analytics/learning/time-to-complete', null, {}, { signal: opts.signal });
}

export async function get_learning_time_series(opts = {}) {
    const qs = _growth_params(opts);
    return api_request('GET', `/analytics/learning/time-series?${qs}`, null, {}, { signal: opts.signal });
}

export async function get_engagement_feature_usage(opts = {}) {
    const days = opts.days ?? 30;
    return api_request('GET', `/analytics/engagement/feature-usage?days=${days}`, null, {}, { signal: opts.signal });
}

export async function record_client_event(event_name, metadata = null) {
    return api_request('POST', '/analytics/client-event', { event_name, metadata });
}

export async function get_revenue_refunds(opts = {}) {
    const days = opts.days ?? 30;
    return api_request('GET', `/analytics/revenue/refunds?days=${days}`, null, {}, { signal: opts.signal });
}

export async function get_users_by_signup_source(opts = {}) {
    return api_request('GET', '/analytics/users/breakdown/signup-source', null, {}, { signal: opts.signal });
}

export async function get_events_by_device(opts = {}) {
    const days = opts.days ?? 30;
    return api_request('GET', `/analytics/events/breakdown/device?days=${days}`, null, {}, { signal: opts.signal });
}

export async function get_events_by_browser(opts = {}) {
    const days = opts.days ?? 30;
    return api_request('GET', `/analytics/events/breakdown/browser?days=${days}`, null, {}, { signal: opts.signal });
}

export async function get_events_by_os(opts = {}) {
    const days = opts.days ?? 30;
    return api_request('GET', `/analytics/events/breakdown/os?days=${days}`, null, {}, { signal: opts.signal });
}

export async function get_engagement_journey(opts = {}) {
    const params = new URLSearchParams();
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.days) params.set('days', String(opts.days));
    const qs = params.toString();
    return api_request('GET', `/analytics/engagement/journey${qs ? `?${qs}` : ''}`, null, {}, { signal: opts.signal });
}


// ── Quizzes: Public Endpoints ───────────────────────────────────────────────

export async function list_public_academic_years() {
    return await api_request('GET', '/public/academic-years');
}

export async function list_public_modules() {
    return await api_request('GET', '/public/modules');
}

export async function list_public_submodules(module_id) {
    return await api_request('GET', `/public/modules/${module_id}/submodules`);
}

export async function list_public_tags() {
    return await api_request('GET', '/public/tags');
}

export async function list_public_difficulties() {
    return await api_request('GET', '/public/difficulties');
}

export async function list_public_exam_types() {
    return await api_request('GET', '/public/exam-types');
}

export async function list_public_sources() {
    return await api_request('GET', '/public/sources');
}


export async function bulk_import_questions({ module_id, lecture_id, mode, payload, dry_run }) {
    return await api_request('POST', '/questions/bulk-import', {
        module_id,
        lecture_id,
        mode,
        payload,
        dry_run,
    });
}


// ── Quiz: Difficulty / ExamType / Source CRUD ───────────────────────────────

export async function list_difficulties(page = 1, page_size = 20, search = '') {
    let url = `/difficulties?page=${page}&page_size=${page_size}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    return await api_request('GET', url);
}

export async function create_difficulty(data) {
    return await api_request('POST', '/difficulties', data);
}

export async function get_difficulty(id) {
    return await api_request('GET', `/difficulties/${id}`);
}

export async function update_difficulty(id, data) {
    return await api_request('PUT', `/difficulties/${id}`, data);
}

export async function delete_difficulty(id) {
    return await api_request('DELETE', `/difficulties/${id}`);
}

export async function list_exam_types(page = 1, page_size = 20, search = '') {
    let url = `/exam-types?page=${page}&page_size=${page_size}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    return await api_request('GET', url);
}

export async function create_exam_type(data) {
    return await api_request('POST', '/exam-types', data);
}

export async function get_exam_type(id) {
    return await api_request('GET', `/exam-types/${id}`);
}

export async function update_exam_type(id, data) {
    return await api_request('PUT', `/exam-types/${id}`, data);
}

export async function delete_exam_type(id) {
    return await api_request('DELETE', `/exam-types/${id}`);
}

export async function list_sources(page = 1, page_size = 20, search = '') {
    let url = `/sources?page=${page}&page_size=${page_size}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    return await api_request('GET', url);
}

export async function create_source(data) {
    return await api_request('POST', '/sources', data);
}

export async function get_source(id) {
    return await api_request('GET', `/sources/${id}`);
}

export async function update_source(id, data) {
    return await api_request('PUT', `/sources/${id}`, data);
}

export async function delete_source(id) {
    return await api_request('DELETE', `/sources/${id}`);
}


// ── Quiz: Academic Years CRUD ───────────────────────────────────────────────

export async function list_academic_years(page = 1, page_size = 20, search = '') {
    let url = `/academic-years?page=${page}&page_size=${page_size}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    return await api_request('GET', url);
}

export async function create_academic_year(data) {
    return await api_request('POST', '/academic-years', data);
}

export async function get_academic_year(id) {
    return await api_request('GET', `/academic-years/${id}`);
}

export async function update_academic_year(id, data) {
    return await api_request('PUT', `/academic-years/${id}`, data);
}

export async function delete_academic_year(id) {
    return await api_request('DELETE', `/academic-years/${id}`);
}


// ── Quiz: Modules CRUD ──────────────────────────────────────────────────────

/** Appends the non-empty entries of `filters` to `url` as query params. */
function with_filters(url, filters = {}) {
    for (const [key, value] of Object.entries(filters)) {
        if (value !== null && value !== undefined && value !== '') url += `&${key}=${encodeURIComponent(value)}`;
    }
    return url;
}

/** filters: { study_year_id?, faculty_id?, is_published? } */
export async function list_modules(page = 1, page_size = 20, search = '', filters = {}) {
    let url = `/modules?page=${page}&page_size=${page_size}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    return await api_request('GET', with_filters(url, filters));
}

export async function create_module(data) {
    return await api_request('POST', '/modules', data);
}

export async function get_module(id) {
    return await api_request('GET', `/modules/${id}`);
}

export async function update_module(id, data) {
    return await api_request('PUT', `/modules/${id}`, data);
}

export async function delete_module(id) {
    return await api_request('DELETE', `/modules/${id}`);
}


// ── Quiz: SubModules CRUD ───────────────────────────────────────────────────

/** filters: { module_id?, study_year_id?, faculty_id?, is_published? } */
export async function list_submodules(page = 1, page_size = 20, search = '', filters = {}) {
    let url = `/submodules?page=${page}&page_size=${page_size}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    return await api_request('GET', with_filters(url, filters));
}

export async function create_submodule(data) {
    return await api_request('POST', '/submodules', data);
}

export async function get_submodule(id) {
    return await api_request('GET', `/submodules/${id}`);
}

export async function update_submodule(id, data) {
    return await api_request('PUT', `/submodules/${id}`, data);
}

export async function delete_submodule(id) {
    return await api_request('DELETE', `/submodules/${id}`);
}


// ── Quiz: Questions CRUD ────────────────────────────────────────────────────

export async function list_questions(page = 1, page_size = 20, search = '', extra = {}) {
    let url = `/questions?page=${page}&page_size=${page_size}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (extra.question_type) url += `&question_type=${encodeURIComponent(extra.question_type)}`;
    if (extra.difficulty) url += `&difficulty=${encodeURIComponent(extra.difficulty)}`;
    if (extra.submodule_id != null) url += `&submodule_id=${extra.submodule_id}`;
    if (extra.lecture_id != null) url += `&lecture_id=${extra.lecture_id}`;
    if (extra.module_id != null) url += `&module_id=${extra.module_id}`;
    if (extra.study_year_id != null) url += `&study_year_id=${extra.study_year_id}`;
    if (extra.academic_year_id != null) url += `&academic_year_id=${extra.academic_year_id}`;
    if (extra.exam_session) url += `&exam_session=${encodeURIComponent(extra.exam_session)}`;
    if (extra.sort) url += `&sort=${encodeURIComponent(extra.sort)}&sort_dir=${extra.sort_dir === 'asc' ? 'asc' : 'desc'}`;
    if (extra.is_published === true) url += `&is_published=true`;
    else if (extra.is_published === false) url += `&is_published=false`;
    return await api_request('GET', url);
}

/**
 * Lists question illustrations — one row per (question, embedded image) pair.
 * @param {number} page
 * @param {number} page_size
 * @param {string} search
 * @param {{module_id?: number|null, submodule_id?: number|null, study_year_id?: number|null, is_published?: boolean|null}} extra
 */
export async function list_question_illustrations(page = 1, page_size = 20, search = '', extra = {}) {
    let url = `/question-illustrations?page=${page}&page_size=${page_size}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (extra.module_id != null) url += `&module_id=${extra.module_id}`;
    if (extra.submodule_id != null) url += `&submodule_id=${extra.submodule_id}`;
    if (extra.study_year_id != null) url += `&study_year_id=${extra.study_year_id}`;
    if (extra.is_published === true) url += `&is_published=true`;
    else if (extra.is_published === false) url += `&is_published=false`;
    return await api_request('GET', url);
}

export async function create_question(data) {
    return await api_request('POST', '/questions', data);
}

export async function get_question(id) {
    return await api_request('GET', `/questions/${id}`);
}

export async function update_question(id, data) {
    return await api_request('PUT', `/questions/${id}`, data);
}

export async function delete_question(id) {
    return await api_request('DELETE', `/questions/${id}`);
}

export async function regenerate_question_explanation(id) {
    return await api_request('POST', `/questions/${id}/regenerate-explanation`);
}

/**
 * Stream regenerate-explanation. `onChunk(text)` per delta,
 * `onDone(payload)` on completion with `{ explanation }`.
 *
 * @param {number|string} id
 * @param {{ onChunk: (t: string) => void, onDone?: (payload: any) => void, signal?: AbortSignal }} handlers
 */
export async function regenerate_question_explanation_stream(id, { onChunk, onDone, signal }) {
    const csrf = get_cookie('csrftoken');
    const headers = { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...auth_headers() };
    if (csrf) headers['X-CSRFToken'] = csrf;
    const resp = await fetch(`/api/questions/${id}/regenerate-explanation/stream`, {
        method: 'POST',
        credentials: auth_credentials(),
        headers,
        signal,
    });
    if (!resp.ok || !resp.body) {
        let detail = `HTTP ${resp.status}`;
        try { detail = (await resp.json())?.detail || detail; } catch (_) { /* ignore */ }
        const err = new Error(detail);
        err.detail = detail;
        throw err;
    }
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
            const raw = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            for (const line of raw.split('\n')) {
                if (!line.startsWith('data:')) continue;
                const payload = line.slice(5).trim();
                if (!payload) continue;
                let evt;
                try { evt = JSON.parse(payload); } catch { continue; }
                if (evt.type === 'chunk' && typeof evt.text === 'string') {
                    onChunk(evt.text);
                } else if (evt.type === 'done') {
                    onDone?.(evt);
                    return;
                } else if (evt.type === 'error') {
                    const err = new Error(evt.detail || 'Stream error');
                    err.detail = evt.detail;
                    throw err;
                }
            }
        }
    }
    onDone?.({});
}


// ── Quiz: Question Tags ─────────────────────────────────────────────────────

export async function add_question_tags(question_id, tag_ids) {
    return await api_request('POST', `/questions/${question_id}/tags`, { tag_ids });
}

export async function set_question_tags(question_id, tag_ids) {
    return await api_request('PUT', `/questions/${question_id}/tags`, { tag_ids });
}

export async function remove_question_tag(question_id, tag_id) {
    return await api_request('DELETE', `/questions/${question_id}/tags/${tag_id}`);
}


// ── Quiz: Tags CRUD ─────────────────────────────────────────────────────────

export async function list_tags(page = 1, page_size = 20, search = '') {
    let url = `/tags?page=${page}&page_size=${page_size}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    return await api_request('GET', url);
}

export async function create_tag(data) {
    return await api_request('POST', '/tags', data);
}

export async function get_tag(id) {
    return await api_request('GET', `/tags/${id}`);
}

export async function update_tag(id, data) {
    return await api_request('PUT', `/tags/${id}`, data);
}

export async function delete_tag(id) {
    return await api_request('DELETE', `/tags/${id}`);
}


// ── Reading: TTA v2 ─────────────────────────────────────────────────────────

/**
 * Fetches a module's lectures ranked by how many questions they hold.
 * @param {number} module_id The ID of the module.
 * @returns {Promise<object>} A promise resolving to `{ total_questions, items }`.
 */
export async function get_module_question_frequency(module_id) {
    return await api_request('GET', `/reading/modules/${module_id}/question-frequency`);
}

export async function list_reading_modules(params = {}) {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== null && v !== '') sp.set(k, v);
    }
    const qs = sp.toString();
    return await api_request('GET', `/reading/modules${qs ? '?' + qs : ''}`);
}

export async function get_reading_module(id) {
    return await api_request('GET', `/reading/modules/${id}`);
}

export async function get_reading_submodule(id) {
    return await api_request('GET', `/reading/submodules/${id}`);
}

export async function get_reading_question(id) {
    return await api_request('GET', `/reading/questions/${id}`);
}

/**
 * High-priority, deduplicated learner request for one missing English question.
 * The endpoint never accepts model/force options from the client.
 */
export async function request_question_translation(question_id) {
    return await api_request('POST', `/questions/${question_id}/translation`);
}

/** Poll translation state without creating new inference work. */
export async function get_question_translation_status(question_id) {
    return await api_request('GET', `/questions/${question_id}/translation`);
}

export async function search_reading_questions(params = {}) {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== null && v !== '') sp.set(k, v);
    }
    const qs = sp.toString();
    return await api_request('GET', `/reading/questions${qs ? '?' + qs : ''}`);
}

// Bookmarks

export async function list_bookmarks() {
    return await api_request('GET', '/reading/bookmarks');
}

export async function create_bookmark(question_id) {
    return await api_request('POST', '/reading/bookmarks', { question_id });
}

export async function delete_bookmark(bookmark_id) {
    return await api_request('DELETE', `/reading/bookmarks/${bookmark_id}`);
}

export async function delete_bookmark_by_question(question_id) {
    return await api_request('DELETE', `/reading/bookmarks/by-question/${question_id}`);
}

// Highlights

export async function list_highlights(question_id, language = 'fr') {
    const params = new URLSearchParams({
        question_id: String(question_id),
        language: language === 'en' ? 'en' : 'fr',
    });
    return await api_request('GET', `/reading/highlights?${params.toString()}`);
}

export async function list_all_highlights() {
    return await api_request('GET', '/reading/highlights/all');
}

export async function create_highlight(data) {
    return await api_request('POST', '/reading/highlights', data);
}

export async function update_highlight(highlight_id, data) {
    return await api_request('PATCH', `/reading/highlights/${highlight_id}`, data);
}

export async function delete_highlight(highlight_id) {
    return await api_request('DELETE', `/reading/highlights/${highlight_id}`);
}

// Progress

export async function list_reading_progress() {
    return await api_request('GET', '/reading/progress');
}

export async function upsert_reading_progress(data) {
    return await api_request('PUT', '/reading/progress', data);
}

export async function get_reading_progress(lecture_id) {
    return await api_request('GET', `/reading/progress/${lecture_id}`);
}

// ── Reading Lectures (TTA v6) ────────────────────────────────────────────

export async function get_reading_lecture(lecture_id) {
    return await api_request('GET', `/reading/lectures/${lecture_id}`);
}

export async function list_public_lectures(submodule_id) {
    return await api_request('GET', `/public/submodules/${submodule_id}/lectures`);
}

// ── Admin Reading Lectures (TTA v6) ──────────────────────────────────────

export async function list_admin_lectures(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return await api_request('GET', `/admin/lectures${qs ? '?' + qs : ''}`);
}

export async function create_reading_lecture(data) {
    return await api_request('POST', '/admin/lectures', data);
}

export async function update_reading_lecture(lecture_id, data) {
    return await api_request('PUT', `/admin/lectures/${lecture_id}`, data);
}

export async function delete_reading_lecture(lecture_id) {
    return await api_request('DELETE', `/admin/lectures/${lecture_id}`);
}

// ── Quiz Sessions (TTA v3) ────────────────────────────────────────────────

export async function create_quiz_session(submodule_id, mode = 'PRACTICE') {
    return await api_request('POST', '/sessions', { submodule_id, mode });
}

export async function create_playlist_session(playlist_id, mode = 'PLAYLIST') {
    return await api_request('POST', '/sessions', { playlist_id, mode });
}

export async function list_quiz_sessions() {
    return await api_request('GET', '/sessions');
}

export async function get_quiz_session(id) {
    return await api_request('GET', `/sessions/${id}`);
}

export async function get_session_question(session_id, question_id) {
    return await api_request('GET', `/sessions/${session_id}/questions/${question_id}`);
}

export async function get_active_session() {
    return await api_request('GET', '/sessions/active');
}

export async function delete_quiz_session(id) {
    return await api_request('DELETE', `/sessions/${id}`);
}

export async function pause_session(id, { keepalive = false } = {}) {
    return await api_request('POST', `/sessions/${id}/pause`, null, {}, { keepalive });
}

/**
 * Fire-and-forget POST that survives page unload.
 * Uses `keepalive: true` so the request completes even after the tab closes.
 * Unlike `sendBeacon`, we can attach the CSRF header Django requires.
 * Returns immediately; failures are swallowed.
 */
export function beacon_post(path, data = null) {
    const csrf_token = get_cookie('csrftoken');
    const headers = { Accept: 'application/json', ...auth_headers() };
    if (csrf_token) headers['X-CSRFToken'] = csrf_token;
    if (data !== null) headers['Content-Type'] = 'application/json';
    try {
        fetch(`/api${path}`, {
            method: 'POST',
            headers,
            credentials: auth_credentials(),
            keepalive: true,
            body: data !== null ? JSON.stringify(data) : undefined,
        }).catch(() => {});
    } catch { /* keepalive quota exceeded, etc — silent */ }
}

export async function resume_session(id) {
    return await api_request('POST', `/sessions/${id}/resume`);
}

export async function finish_session(id) {
    return await api_request('POST', `/sessions/${id}/finish`);
}

export async function save_session_answer(session_id, question_id, selected_option_ids) {
    return await api_request('POST', `/sessions/${session_id}/answers`, { question_id, selected_option_ids });
}

export async function get_session_answers(session_id) {
    return await api_request('GET', `/sessions/${session_id}/answers`);
}

export async function get_session_summary(id) {
    return await api_request('GET', `/sessions/${id}/summary`);
}

export async function get_session_review(id) {
    return await api_request('GET', `/sessions/${id}/review`);
}

// ── Student Dashboard (TTA v4) ───────────────────────────────────────────────

export async function get_dashboard() {
    return await api_request('GET', '/dashboard');
}

export async function get_question_statistics(question_id) {
    return await api_request('GET', `/questions/${question_id}/statistics`);
}

export async function get_question_community_stats(question_id) {
    return await api_request('GET', `/questions/${question_id}/community`);
}

export async function get_community_comparison(session_id) {
    return await api_request('GET', `/sessions/${session_id}/community-comparison`);
}

export async function get_my_statistics() {
    return await api_request('GET', '/me/statistics');
}

export async function get_my_activity_calendar(opts = {}) {
    const days = opts.days ?? 365;
    return api_request('GET', `/me/activity/calendar?days=${days}`, null, {}, { signal: opts.signal });
}

// ── Admin Quiz Stats (TTA v4) ────────────────────────────────────────────────

export async function get_system_quiz_stats() {
    return await api_request('GET', '/statistics/system');
}

export async function rebuild_statistics() {
    return await api_request('POST', '/statistics/rebuild');
}

// ── Playlists (TTA v5) ──────────────────────────────────────────────────

export async function list_playlists() {
    return await api_request('GET', '/playlists');
}

export async function create_playlist(data) {
    return await api_request('POST', '/playlists', data);
}

export async function get_playlist(id) {
    return await api_request('GET', `/playlists/${id}`);
}

export async function update_playlist(id, data) {
    return await api_request('PUT', `/playlists/${id}`, data);
}

export async function delete_playlist(id) {
    return await api_request('DELETE', `/playlists/${id}`);
}

export async function duplicate_playlist(id) {
    return await api_request('POST', `/playlists/${id}/duplicate`);
}

export async function add_playlist_item(playlist_id, question_id) {
    return await api_request('POST', `/playlists/${playlist_id}/questions`, { question_id });
}

export async function remove_playlist_item(playlist_id, question_id) {
    return await api_request('DELETE', `/playlists/${playlist_id}/questions/${question_id}`);
}

export async function reorder_playlist_items(playlist_id, item_ids) {
    return await api_request('PATCH', `/playlists/${playlist_id}/reorder`, { item_ids });
}

export async function create_playlist_from_lectures(data) {
    return await api_request('POST', '/playlists/from-lectures', data);
}

export async function add_lectures_to_playlist(playlist_id, lecture_ids) {
    return await api_request('POST', `/playlists/${playlist_id}/lectures`, { lecture_ids });
}

export async function remove_lectures_from_playlist(playlist_id, lecture_ids) {
    return await api_request('DELETE', `/playlists/${playlist_id}/lectures`, { lecture_ids });
}

export async function list_playlist_items(playlist_id) {
    return await api_request('GET', `/playlists/${playlist_id}/questions`);
}

// ── Smart Collections (TTA v5) ──────────────────────────────────────────

export async function list_collections() {
    return await api_request('GET', '/me/collections');
}

export async function get_collection(slug) {
    return await api_request('GET', `/me/collections/${slug}`);
}

export async function get_questions_batch(question_ids) {
    return await api_request('POST', '/questions/batch', { question_ids });
}

// ── Landing (public) ─────────────────────────────────────────────────────
export async function get_landing_page(slug = 'home', { draft = false } = {}) {
    const qs = draft ? '?draft=1' : '';
    return await api_request('GET', `/landing/${slug}${qs}`);
}

export async function get_youtube_stats() {
    return await api_request('GET', '/public/youtube/stats');
}

// ── Catalog (public) ─────────────────────────────────────────────────────
// What a visitor without a session can read. Narrower than the authenticated
// course/book endpoints: no subscription state, no progress, no media. The
// server renders these same rows into the page for crawlers (main/seo), so
// the two must keep agreeing.
export async function list_public_courses(year = null) {
    const qs = year ? `?year=${year}` : '';
    return await api_request('GET', `/public/courses${qs}`);
}

export async function get_public_course(course_id) {
    return await api_request('GET', `/public/courses/${course_id}`);
}

export async function list_public_books(year = null) {
    const qs = year ? `?year=${year}` : '';
    return await api_request('GET', `/public/books${qs}`);
}

export async function get_public_book(book_id) {
    return await api_request('GET', `/public/books/${book_id}`);
}

// ── Landing admin (superuser) ────────────────────────────────────────────
export async function admin_get_landing(slug = 'home') {
    return await api_request('GET', `/admin/landing/${slug}`);
}

export async function admin_create_landing_block(slug, { block_type, content = {}, order = null, enabled = true }) {
    return await api_request('POST', `/admin/landing/${slug}/blocks`, { block_type, content, order, enabled });
}

export async function admin_update_landing_block(block_id, patch) {
    return await api_request('PATCH', `/admin/landing/blocks/${block_id}`, patch);
}

export async function admin_delete_landing_block(block_id) {
    return await api_request('DELETE', `/admin/landing/blocks/${block_id}`);
}

export async function admin_reorder_landing(slug, orderedBlockIds) {
    return await api_request('POST', `/admin/landing/${slug}/reorder`, { order: orderedBlockIds });
}

export async function admin_publish_landing(slug = 'home') {
    return await api_request('POST', `/admin/landing/${slug}/publish`);
}

// ── Question Reports (TTA v5) ───────────────────────────────────────────

export async function create_question_report(data) {
    return await api_request('POST', '/question-reports', data);
}

export async function list_my_reports() {
    return await api_request('GET', '/question-reports/me');
}

export async function get_question_report(id) {
    return await api_request('GET', `/question-reports/${id}`);
}

// ── Admin Reports (TTA v5) ──────────────────────────────────────────────

export async function admin_list_reports(page = 1, page_size = 20, status = null, reason = null) {
    let url = `/admin/question-reports?page=${page}&page_size=${page_size}`;
    if (status) url += `&status=${encodeURIComponent(status)}`;
    if (reason) url += `&reason=${encodeURIComponent(reason)}`;
    return await api_request('GET', url);
}

export async function admin_update_report(report_id, data) {
    return await api_request('PATCH', `/admin/question-reports/${report_id}`, data);
}

export async function admin_report_statistics() {
    return await api_request('GET', '/admin/question-reports/statistics');
}

/**
 * Ask the server-side AI to enhance markdown formatting for a description.
 * @param {string} text
 * @param {string} [kind]
 * @returns {Promise<{response_data: {text: string}}>}
 */
export async function ai_enhance_description(text, kind = 'course') {
    return await api_request('POST', '/ai/enhance-description', { text, kind });
}

/**
 * Stream AI-enhanced markdown chunks. Calls `onChunk(text)` for each token,
 * `onDone()` when finished. Rejects with an Error on transport/API failure.
 *
 * @param {string} text
 * @param {string} kind
 * @param {{ onChunk: (t: string) => void, onDone?: () => void, signal?: AbortSignal }} handlers
 */
export async function ai_enhance_description_stream(text, kind, { onChunk, onDone, signal }) {
    const csrf = get_cookie('csrftoken');
    const headers = { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...auth_headers() };
    if (csrf) headers['X-CSRFToken'] = csrf;
    const resp = await fetch(`/api/ai/enhance-description/stream`, {
        method: 'POST',
        credentials: auth_credentials(),
        headers,
        body: JSON.stringify({ text, kind }),
        signal,
    });
    if (!resp.ok || !resp.body) {
        let detail = `HTTP ${resp.status}`;
        try { detail = (await resp.json())?.detail || detail; } catch (_) { /* ignore */ }
        const err = new Error(detail);
        err.detail = detail;
        throw err;
    }
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
            const raw = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            for (const line of raw.split('\n')) {
                if (!line.startsWith('data:')) continue;
                const payload = line.slice(5).trim();
                if (!payload) continue;
                let evt;
                try { evt = JSON.parse(payload); } catch { continue; }
                if (evt.type === 'chunk' && typeof evt.text === 'string') {
                    onChunk(evt.text);
                } else if (evt.type === 'done') {
                    onDone?.();
                    return;
                } else if (evt.type === 'error') {
                    const err = new Error(evt.detail || 'Stream error');
                    err.detail = evt.detail;
                    throw err;
                }
            }
        }
    }
    onDone?.();
}

export async function get_ai_structure_prompt() {
    return await api_request('GET', '/ai/structure-questions/prompt');
}

export async function ai_structure_questions(text, { onChunk, onDone, signal }) {
    const csrf = get_cookie('csrftoken');
    const headers = { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...auth_headers() };
    if (csrf) headers['X-CSRFToken'] = csrf;
    const resp = await fetch(`/api/ai/structure-questions/stream`, {
        method: 'POST',
        credentials: auth_credentials(),
        headers,
        body: JSON.stringify({ text, kind: 'questions' }),
        signal,
    });
    if (!resp.ok || !resp.body) {
        let detail = `HTTP ${resp.status}`;
        try { detail = (await resp.json())?.detail || detail; } catch (_) { /* ignore */ }
        const err = new Error(detail);
        err.detail = detail;
        throw err;
    }
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
            const raw = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            for (const line of raw.split('\n')) {
                if (!line.startsWith('data:')) continue;
                const payload = line.slice(5).trim();
                if (!payload) continue;
                let evt;
                try { evt = JSON.parse(payload); } catch { continue; }
                if (evt.type === 'chunk' && typeof evt.text === 'string') {
                    onChunk(evt.text);
                } else if (evt.type === 'done') {
                    onDone?.();
                    return;
                } else if (evt.type === 'error') {
                    const err = new Error(evt.detail || 'Stream error');
                    err.detail = evt.detail;
                    throw err;
                }
            }
        }
    }
    onDone?.();
}

// ── Access rules (admin) ──────────────────────────────────────────

/** Lists every explicit access rule (site default excluded). */
export async function list_access_rules(page = 1, page_size = 20, scope = '') {
    let url = `/access/rules?page=${page}&page_size=${page_size}`;
    if (scope) url += `&scope=${encodeURIComponent(scope)}`;
    return await api_request('GET', url);
}

export async function get_default_access_rule() {
    return await api_request('GET', '/access/rules/default');
}

export async function update_default_access_rule(data) {
    return await api_request('PUT', '/access/rules/default', data);
}

/** Resolution chain for one object: `scope` is module | submodule | question. */
export async function get_access_chain(scope, object_id) {
    return await api_request('GET', `/access/rules/${scope}/${object_id}`);
}

export async function upsert_access_rule(scope, object_id, data) {
    return await api_request('PUT', `/access/rules/${scope}/${object_id}`, data);
}

export async function delete_access_rule(scope, object_id) {
    return await api_request('DELETE', `/access/rules/${scope}/${object_id}`);
}

/** Modules a course's curriculum covers. Does not unlock questions; bundles do. */
export async function get_course_modules(course_id) {
    return await api_request('GET', `/access/courses/${course_id}/modules`);
}

export async function set_course_modules(course_id, module_ids) {
    return await api_request('PUT', `/access/courses/${course_id}/modules`, { module_ids });
}


// ── Question bundles (admin) ──────────────────────────────────────

/**
 * Bundles are what a key unlocks questions through: modules, submodules and/or single questions.
 * filters: { study_year_id?, module_id? } — a bundle matches when it sells anything under them.
 */
export async function list_bundles(page = 1, page_size = 20, search = '', filters = {}) {
    let url = with_filters(`/bundles?page=${page}&page_size=${page_size}`, filters);
    if (search) url += `&search=${encodeURIComponent(search)}`;
    return await api_request('GET', url);
}

export async function get_bundle(id) {
    return await api_request('GET', `/bundles/${id}`);
}

/** data: { title, description, price, module_ids, submodule_ids, question_ids } */
export async function create_bundle(data) {
    return await api_request('POST', '/bundles', data);
}

export async function update_bundle(id, data) {
    return await api_request('PUT', `/bundles/${id}`, data);
}

export async function delete_bundle(id) {
    return await api_request('DELETE', `/bundles/${id}`);
}

/** Modules no bundle sells as a whole: [{ id, name, study_year_name }]. */
export async function list_uncovered_modules() {
    return await api_request('GET', '/bundles/uncovered-modules');
}

/** Creates one bundle (price 0, covering the whole module) per module that has none. */
export async function generate_module_bundles() {
    return await api_request('POST', '/bundles/generate-for-modules');
}

/** What a bundle the signed-in student holds unlocks: totals plus per-module and per-submodule counts. */
export async function get_bundle_contents(id) {
    return await api_request('GET', `/bundles/${id}/contents`);
}


// ── Translation operations admin ────────────────────────────────────────────

export async function get_translation_overview() {
    return api_request('GET', '/admin/translations/overview');
}

export async function get_translation_scopes() {
    return api_request('GET', '/admin/translations/scopes');
}

export async function list_translation_questions({ page = 1, page_size = 40, search = '', module_id = '', submodule_id = '', state = '' } = {}) {
    const params = new URLSearchParams({ page: String(page), page_size: String(page_size) });
    if (search) params.set('search', search);
    if (module_id) params.set('module_id', String(module_id));
    if (submodule_id) params.set('submodule_id', String(submodule_id));
    if (state) params.set('state', state);
    return api_request('GET', `/admin/translations/questions?${params.toString()}`);
}

export async function list_translation_jobs(page = 1, page_size = 20) {
    return api_request('GET', `/admin/translations/jobs?page=${page}&page_size=${page_size}`);
}

export async function get_translation_job(job_id, item_page = 1, item_page_size = 100) {
    return api_request('GET', `/admin/translations/jobs/${job_id}?item_page=${item_page}&item_page_size=${item_page_size}`);
}

export async function create_translation_job(data) {
    return api_request('POST', '/admin/translations/jobs', data);
}

export async function cancel_translation_job(job_id) {
    return api_request('POST', `/admin/translations/jobs/${job_id}/cancel`);
}

export async function retry_translation_job(job_id) {
    return api_request('POST', `/admin/translations/jobs/${job_id}/retry`);
}


// ── Online payments (Slick-Pay) ─────────────────────────────────────────────

/**
 * Plans a student can buy, each with the Slick-Pay fee already added (`total`).
 * Narrowed to what they were trying to open when given an item
 * (`content_type` + `object_id`), a `question_id`, a `module_id`, a `playlist_id`
 * or a study `year` (its order).
 */
/** Chosen plans for a public offers page (see src/lib/offers.js). No session needed. */
export async function list_public_offers(plan_ids) {
    return api_request('GET', `/public/offers?plans=${plan_ids.join(',')}`);
}

export async function list_payment_plans(context = {}) {
    const params = new URLSearchParams();
    for (const key of ['content_type', 'object_id', 'question_id', 'module_id', 'playlist_id', 'year']) {
        if (context[key]) params.set(key, context[key]);
    }
    const query = params.toString();
    return api_request('GET', `/payments/plans${query ? `?${query}` : ''}`);
}

/**
 * Opens a Slick-Pay invoice for a plan. `accept_terms` records that the buyer
 * confirmed the order and accepted the terms of sale; the server refuses without it.
 */
export async function start_payment_checkout(plan_id, { accept_terms = false } = {}) {
    return api_request('POST', '/payments/checkout', { plan_id, accept_terms });
}

/** Seller identity, terms version and fee rule for the public terms page. */
export async function get_public_terms_of_sale() {
    return api_request('GET', '/public/terms-of-sale');
}

/** The terms of sale checkout asks the buyer to accept: `{ version, url }`. */
export async function get_payment_terms() {
    return api_request('GET', '/payments/terms');
}

export async function admin_payment_events(id) {
    return api_request('GET', `/admin/payments/${id}/events`);
}

export async function admin_resend_receipt(id) {
    return api_request('POST', `/admin/payments/${id}/resend-receipt`);
}

/** Re-checks a payment with Slick-Pay; the server grants access once it is paid. */
export async function get_payment(id) {
    return api_request('GET', `/payments/${id}`);
}

export async function admin_list_payment_plans() {
    return api_request('GET', '/admin/payment-plans');
}

export async function admin_create_payment_plan(data) {
    return api_request('POST', '/admin/payment-plans', data);
}

export async function admin_update_payment_plan(id, data) {
    return api_request('PUT', `/admin/payment-plans/${id}`, data);
}

export async function admin_delete_payment_plan(id) {
    return api_request('DELETE', `/admin/payment-plans/${id}`);
}

export async function admin_refresh_payment(id) {
    return api_request('POST', `/admin/payments/${id}/refresh`);
}
